import type { TripSummary } from '@trippath/shared';

/** A trip in the list of trips, with how deep it sits under the trip its people set out on. */
export interface Placed {
  trip: TripSummary;
  depth: number;
}

/**
 * The trips as a tree: each trip whose parent is not listed stands first, as the list has it,
 * the trips branching off it under it in the family's own order, each before its branches.
 */
export function treeOf(trips: readonly TripSummary[]): Placed[] {
  const listed = (id: string | null) => trips.some((trip) => trip.id === id);
  const depthOf = (trip: TripSummary): number => {
    const parent = trips.find((other) => other.id === trip.parent);
    return parent ? 1 + depthOf(parent) : 0;
  };
  const under = (root: TripSummary): TripSummary[] =>
    trips
      .filter((trip) => trip.parent === root.id)
      .flatMap((branch) => [branch, ...under(branch)]);
  return trips
    .filter((trip) => !listed(trip.parent))
    .flatMap((root) => [root, ...under(root).toSorted((a, b) => a.place - b.place)])
    .map((trip) => ({ trip, depth: depthOf(trip) }));
}
